import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const stored = window.localStorage.getItem('food_science_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('food_science_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => setTarget(getInitialTarget()), []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('food_science_countdown_target', nextTarget.toString());
        } catch {}
        setTarget(nextTarget);
        setRemaining(24 * 60 * 60 * 1000);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-gradient-to-b from-[#2a0808] to-[#1a0404] border border-red-500/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(239,68,68,0.15)]" data-id="element-43" data-mp="1kzabunaitkws">
      <div className="text-center" data-id="element-44" data-mp="2iiln6m5x9cr1">
        <div className="flex items-center justify-center gap-2 text-red-400 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-45" data-mp="2sczkfehyns1p">
          <AlarmClock size={20} className="animate-pulse" data-id="element-46" data-mp="2nefsmxgorm1f" />
          <span data-id="element-47" data-mp="2jb9rg48fkl1t">Launch price ends soon</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-48" data-mp="2jdvze4z8h2kk">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-49" data-mp="2ecose84x07ey" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-50" data-mp="29e52xxr44dhu">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-51" data-mp="24nexylmlmcno" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-52" data-mp="24lls38jt7wgq">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-53" data-mp="1zp3nc8ohby4e" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-gray-200 mb-8" data-id="element-54" data-mp="1vmqkpt514gbu">
          After this, the price goes up — and it won&apos;t come back down.
        </p>
        <div className="space-y-2" data-id="element-55" data-mp="1loarnkmfywzp">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-56" data-mp="1lo12fz980tv2">
            <span className="text-red-300" data-id="element-57" data-mp="1571cpy2sffc">Launch price spots remaining</span>
            <span className="text-red-400" data-id="element-58" data-mp="64tl7i8estx4">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-red-950/60 rounded-full overflow-hidden border border-red-500/30" data-id="element-59" data-mp="b4fy1qkbs85e">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '65%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.6)]" data-id="element-60" data-mp="f5rlbpfq8haf" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-red-300 font-medium" data-id="element-61" data-mp="3njqbgag0p8g0">65% of spots taken</p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-black border-2 border-red-500/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(239,68,68,0.1)]" data-id="element-62" data-mp="2zw0bh0bkrt86">
      <div className="flex items-baseline justify-center gap-1" data-id="element-63" data-mp="2gpj0bfmfa05c">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-64" data-mp="2btvcb3jofd1f">{value}</span>
        <span className="text-xs md:text-sm font-medium text-red-300" data-id="element-65" data-mp="2qi8e4h5g3q3u">{label}</span>
      </div>
    </div>;
}